import { Guid } from "guid-typescript";
import { CALL_TYPE_TO_SUBMISSION_TYPE, DEFAULT_SUBMISSION_TYPE, RM2_COMMERCIAL_SEGMENT, } from "./ConfidenceCallTypeConstants";
import { normalizeAzureSubscriptionGuid } from "./subscriptionGuid";
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const PENDING_SUBMISSION_KEY = "confidenceCallPendingSubmission";
const SUBMISSION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const readPendingSubmissionAttempt = () => {
    const stored = sessionStorage.getItem(PENDING_SUBMISSION_KEY);
    if (stored === null) {
        return undefined;
    }
    const attempt = JSON.parse(stored);
    if (!attempt ||
        typeof attempt !== "object" ||
        !("clientSubmissionId" in attempt) ||
        !("isEU" in attempt)) {
        throw new Error("Invalid pending submission record.");
    }
    const record = attempt;
    if (Object.keys(record).length !== 2 ||
        typeof record.clientSubmissionId !== "string" ||
        !SUBMISSION_ID_PATTERN.test(record.clientSubmissionId) ||
        record.clientSubmissionId === EMPTY_GUID ||
        typeof record.isEU !== "boolean") {
        throw new Error("Invalid pending submission record.");
    }
    return {
        clientSubmissionId: record.clientSubmissionId,
        isEU: record.isEU,
    };
};
export const storePendingSubmissionAttempt = (attempt) => {
    if (readPendingSubmissionAttempt()) {
        throw new Error("An earlier submission still needs confirmation.");
    }
    // Never persist the fingerprint or form: neither is needed to block unsafe replay.
    sessionStorage.setItem(PENDING_SUBMISSION_KEY, JSON.stringify({
        clientSubmissionId: attempt.clientSubmissionId,
        isEU: attempt.isEU,
    }));
    const stored = readPendingSubmissionAttempt();
    if ((stored === null || stored === void 0 ? void 0 : stored.clientSubmissionId) !== attempt.clientSubmissionId ||
        stored.isEU !== attempt.isEU) {
        throw new Error("The pending submission record was not saved.");
    }
};
export const clearPendingSubmissionAttempt = () => {
    sessionStorage.removeItem(PENDING_SUBMISSION_KEY);
    if (sessionStorage.getItem(PENDING_SUBMISSION_KEY) !== null) {
        throw new Error("The pending submission record was not cleared.");
    }
};
export const createSubmissionFingerprint = (value) => {
    return JSON.stringify(value);
};
const projectStakeholder = (stakeholder) => {
    const directoryUser = stakeholder;
    const upn = directoryUser.userPrincipalName ||
        directoryUser.upn ||
        directoryUser.mail ||
        directoryUser.userEmail ||
        directoryUser.uniqueName ||
        "";
    return {
        objectId: directoryUser.id ||
            directoryUser.objectId ||
            directoryUser.aadObjectId ||
            directoryUser.oid ||
            "",
        upn,
        displayName: directoryUser.displayName || directoryUser.name || upn || "",
    };
};
export const createConfidenceCallSubmissionFingerprint = (data) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0;
    const selectedIncidents = ((_a = data.basicInfo) === null || _a === void 0 ? void 0 : _a.selectedIncidents) || [];
    const selectedSupportRequests = ((_b = data.basicInfo) === null || _b === void 0 ? void 0 : _b.selectedSupportRequestNumbers) || [];
    const selectedIncidentOutageMap = ((_c = data.basicInfo) === null || _c === void 0 ? void 0 : _c.selectedIncidentOutageMap) || {};
    const supportTicketTitleMap = ((_d = data.basicInfo) === null || _d === void 0 ? void 0 : _d.supportTicketTitleMap) || {};
    const submissionType = CALL_TYPE_TO_SUBMISSION_TYPE[((_e = data.callType) === null || _e === void 0 ? void 0 : _e.typeKey) || ""] ||
        DEFAULT_SUBMISSION_TYPE;
    const outageSubmissionType = CALL_TYPE_TO_SUBMISSION_TYPE.outage || DEFAULT_SUBMISSION_TYPE;
    const selectedProducts = ((_f = data.productComponent) === null || _f === void 0 ? void 0 : _f.selectedProducts) || [];
    const productsWithSource = selectedProducts.length > 0
        ? selectedProducts
        : (((_g = data.productComponent) === null || _g === void 0 ? void 0 : _g.workloads) || []).map((product) => ({
            id: product,
            name: product,
            source: "Picker",
        }));
    const primaryProductIndex = Math.max(productsWithSource.findIndex((product) => product.source !== "Manual"), 0);
    return createSubmissionFingerprint(Object.assign(Object.assign({ submissionType }, (submissionType === outageSubmissionType
        ? {
            azureSubscriptionId: normalizeAzureSubscriptionGuid((_h = data.basicInfo) === null || _h === void 0 ? void 0 : _h.azureSubscriptionId) || "",
        }
        : {})), { accountInfo: {
            companyName: ((_j = data.basicInfo) === null || _j === void 0 ? void 0 : _j.customerName) || "",
            accountId: ((_k = data.basicInfo) === null || _k === void 0 ? void 0 : _k.customerTpid) || "",
            mSSalesId: ((_l = data.basicInfo) === null || _l === void 0 ? void 0 : _l.customerMsSalesId) || "",
            accountSegment: ((_m = data.basicInfo) === null || _m === void 0 ? void 0 : _m.customerSegment) || RM2_COMMERCIAL_SEGMENT,
            accountType: "Customer",
        }, desiredOutcome: ((_o = data.contextRationale) === null || _o === void 0 ? void 0 : _o.desiredOutcome) || "", description: ((_p = data.contextRationale) === null || _p === void 0 ? void 0 : _p.escalationReasonSummary) || "", businessImpact: ((_q = data.contextRationale) === null || _q === void 0 ? void 0 : _q.businessImpact) || "", customerInfo: {
            country: ((_r = data.basicInfo) === null || _r === void 0 ? void 0 : _r.customerCountry) || "",
            countryAlpha2: ((_s = data.basicInfo) === null || _s === void 0 ? void 0 : _s.customerCountryAlpha2) || "",
            customerSegmentName: ((_t = data.basicInfo) === null || _t === void 0 ? void 0 : _t.customerSegment) || RM2_COMMERCIAL_SEGMENT,
        }, country: {
            id: ((_u = data.basicInfo) === null || _u === void 0 ? void 0 : _u.customerCountryId) || EMPTY_GUID,
            name: ((_v = data.basicInfo) === null || _v === void 0 ? void 0 : _v.customerCountry) || "",
            alpha2: ((_w = data.basicInfo) === null || _w === void 0 ? void 0 : _w.customerCountryAlpha2) || "",
            alpha3: ((_x = data.basicInfo) === null || _x === void 0 ? void 0 : _x.customerCountryAlpha3) || "",
            isEU: Boolean((_y = data.basicInfo) === null || _y === void 0 ? void 0 : _y.customerCountryIsEU),
        }, references: selectedSupportRequests.map((supportRequestNumber) => ({
            referenceType: "SupportTicket",
            referenceId: supportRequestNumber,
            description: supportTicketTitleMap[supportRequestNumber] || "",
            sourceSystem: "SR",
        })), stakeholders: (((_z = data.contextRationale) === null || _z === void 0 ? void 0 : _z.stakeholders) || []).map(projectStakeholder), details: {
            expectedTechnicalDepth: data.technicalDepth || ((_0 = data.contextRationale) === null || _0 === void 0 ? void 0 : _0.technicalDepth) || "",
            linkedIncidents: selectedIncidents.map((incidentId) => {
                var _a;
                return ({
                    incidentNumber: ((_a = incidentId.replace(/^(icm|outage)-/i, "").match(/\d+/)) === null || _a === void 0 ? void 0 : _a[0]) ||
                        incidentId.replace(/^(icm|outage)-/i, ""),
                    sourceSystem: selectedIncidentOutageMap[incidentId] ? "OUTAGE" : "ICM",
                });
            }),
            products: productsWithSource.map((product, index) => ({
                id: product.id,
                name: product.name,
                isPrimary: index === primaryProductIndex,
                source: product.source || "Picker",
            })),
        } }));
};
export const resolveSubmissionAttempt = (currentAttempt, payloadFingerprint, createId = () => Guid.create().toString()) => {
    if ((currentAttempt === null || currentAttempt === void 0 ? void 0 : currentAttempt.payloadFingerprint) === payloadFingerprint) {
        return currentAttempt;
    }
    return {
        clientSubmissionId: createId(),
        payloadFingerprint,
    };
};
